import * as React from 'react'
import * as constants from 'static/constants'
import * as interfaces from 'static/interfaces'

import { tourService, agentService } from 'core/services'
import { AvatarImage } from 'components/atoms'
import Guide from 'components/organisms/Tours/Weekly/Drawer/Guide'
import Memo from 'components/organisms/Tours/Weekly/Drawer/Memo'
import {
  getConnectionConfirmMessage,
  getTourGroupStyleClass,
  getGroupUnassignedTourGuidesByStatus,
  updatedTourByTourGroup,
  updatedTourByOtaReservation,
  getLatestConflictTour,
  sortedOtaReservations,
} from 'utils/weekly'
import SvgIcon from 'components/atoms/SvgIcon'
import moment from 'moment'
import { CancelFormalRequestsModalBody } from './CancelFormalRequestsModalBody'
import { IBuildedTourGroup, IWeeklyOtaReservation, IBuildedTour } from 'utils/weeklyTypes'
import { ConnectTodoIcons } from './ConnectTodoIcons'
import { CancelSoundoutModalBody } from './CancelSoundoutModalBody'
import { Tooltip } from 'components/atoms/Tooltip'
import { ConnectIconButton } from './ConnectIconButton'
import { SoundoutOrFormalRequestModalBody } from './SoundoutOrFormalRequestModalBody'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import { WeeklyInfoCard } from 'components/atoms/weekly/WeeklyInfoCard'
import LinkableText from 'components/atoms/LinkableText'
import { PaymentDetailModal } from './PaymentDetailModal'
import styled from 'styled-components'
import { useTourDrawerContext, GROUP_CATEGORY } from 'core/contexts/tour/TourDrawerContext'
import { ConnectionMessageModalBody } from './ConnectionMessageModalBody'
import { GuideArrivalBadge } from './GuideArrivalBadge'

export declare var window: interfaces.IWindow

interface IProps {
  currentUser: interfaces.IUser
  tour: IBuildedTour
  handleSelectTourGroup: (tourGroup: IBuildedTourGroup) => void
  handleSelectUnassignedOtaReservations: () => void
  refetchWeeklyTour: (tour_id: number) => void
}

export const TourDrawerLeftPanel = ({
  currentUser,
  tour,
  handleSelectTourGroup,
  handleSelectUnassignedOtaReservations,
  refetchWeeklyTour,
}: IProps) => {
  const { allTours, onTourUpdate } = useWeeklyContext()
  const {
    selectedGroupName,
    setSelectedGroupName,
    setGroupSelectedId,
    selectedTourGroup,
    setSelectedTourGroup,
    orderDesc,
    setSelectedOtaReservations,
    groupSelectingResource,
    setGroupSelectingResource,
    setShowGuides,
    setGuideFilterOptions,
  } = useTourDrawerContext()

  const [waitConnecting, setWaitConnecting] = React.useState<boolean>(false)
  const [assigningGuide, setAssigningGuide] = React.useState<boolean>(false)
  const [requestedGuides, approvedGuides, soundoutedGuides] = React.useMemo(() => {
    return getGroupUnassignedTourGuidesByStatus(tour.unAssignedTourGuides)
  }, [tour.unAssignedTourGuides])

  // 正式依頼済みの特殊ガイドはツアーグループにアサインせず「スタッフ(確定済み)」として扱う
  const staffGuides = React.useMemo(() => requestedGuides.filter(tg => tg.agent_rank_id), [
    requestedGuides,
  ])

  const allUnassignedGuides = React.useMemo(() => {
    return [
      ...requestedGuides.filter(tg => !tg.agent_rank_id),
      ...approvedGuides,
      ...soundoutedGuides,
    ]
  }, [requestedGuides, approvedGuides, soundoutedGuides])

  const appliedGuides = React.useMemo(
    () =>
      tour.unAssignedTourGuides.filter(
        tg => tg.request_status === 'guide_applied' && tg.individual_job
      ),
    [tour.unAssignedTourGuides]
  )
  const [rejectingGuide, setRejectingGuide] = React.useState(false)

  const rejectGuide = async (tourGuideId: number, guideName: string) => {
    if (rejectingGuide || !confirm(`${guideName}を見送りますか？（通知は送信されません）`)) {
      return
    }
    setRejectingGuide(true)
    try {
      await tourService.rejectGuide(tour.id, tourGuideId)
      refetchWeeklyTour(tour.id)
    } catch (e) {
      window.flashMessages.addMessage({ text: 'ガイドの見送りに失敗しました。', type: 'error' })
    } finally {
      setRejectingGuide(false)
    }
  }

  // 採番対象のツアー(番号付きのグループがある)かどうか。採番機能リリース前のツアーではグループ指定を表示しない
  const isNumberedTour = tour.last_tour_group_number != null
  // 打診・正式依頼時に指定したツアーグループの番号バッジ。未指定(または削除済み)は破線の「–」で表す
  const requestedTourGroupBadge = React.useCallback(
    (requestedTourGroupId: number | null) => {
      const group = tour.tour_groups.find(g => g.id === requestedTourGroupId)
      return group?.number ? (
        <span
          className="groupNumberBadge"
          title={`ツアーグループ ${group.number}`}
          aria-label={`依頼先ツアーグループ ${group.number}`}
        >
          {group.number}
        </span>
      ) : (
        <span
          className="groupNumberBadge unspecified"
          title="グループ未指定"
          aria-label="依頼先ツアーグループ未指定"
        >
          –
        </span>
      )
    },
    [tour.tour_groups]
  )

  const hasSoundOutedGuides = React.useMemo(() => {
    return [...approvedGuides, ...soundoutedGuides].length > 0
  }, [approvedGuides, soundoutedGuides])

  const isPrivate = React.useMemo(() => {
    return tour.post.post_type === 'private'
  }, [tour.post.post_type])
  const handleSelectCanceledOtaReservations = React.useCallback(() => {
    setSelectedOtaReservations(sortedOtaReservations(tour.canceledOtaReservations, orderDesc))
    setSelectedTourGroup(null)
    setSelectedGroupName(GROUP_CATEGORY.CANCELED)
  }, [tour.canceledOtaReservations, orderDesc])

  const handleAfterUpdateTourGroup = React.useCallback(
    (tour_group, params) => {
      onTourUpdate(updatedTourByTourGroup(tour, tour_group.id, params))
    },
    [tour]
  )
  const showMessageModal = guide => {
    const latestConflictingTour = getLatestConflictTour(
      tour.id,
      guide.id,
      guide.conflicting_tour_ids,
      allTours
    )

    window.globalModal.showModal({
      small: true,
      body: (
        <SoundoutOrFormalRequestModalBody
          agent={currentUser}
          guide={guide}
          tour={tour}
          messageType={'formal'}
          onTourUpdate={onTourUpdate}
          latestConflictingTour={latestConflictingTour}
        />
      ),
    })
  }

  const handleAssignGuide = React.useCallback(
    guideId => {
      setGroupSelectedId(null)
      setTimeout(() => {
        setGuideFilterOptions(constants.DEFAULT_GUIDE_FILTER_OPTIONS)
        setGroupSelectingResource(`tour_guide_${guideId}`)
      }, 200)
    },
    [setGroupSelectedId, setGroupSelectingResource, setGuideFilterOptions]
  )

  const validateAndGetSelectedTourGuide = React.useCallback(() => {
    const selectedTourGuideId = Number(groupSelectingResource.replace('tour_guide_', ''))

    const selectedTourGuide = tour.tour_guides.find(
      tourGuide => tourGuide.id === selectedTourGuideId
    )
    if (!selectedTourGuide?.guide_id) {
      window.flashMessages.addMessage({
        text: '対象のガイドが見つかりませんでした。',
        type: 'error',
      })
      setGroupSelectingResource(null)
      return null
    }

    return selectedTourGuide
  }, [groupSelectingResource, tour.tour_guides, setGroupSelectingResource])

  const assignGuideToTourGroup = React.useCallback(
    async (tourGroupId, tourGuideId, tourId) => {
      try {
        setAssigningGuide(true)
        await tourService.assignGuide(tourGroupId, tourGuideId)
        setGroupSelectingResource(null)
        refetchWeeklyTour(tourId)
      } catch (e) {
        console.error(e)
        window.flashMessages.addMessage({
          text: 'ガイドのアサインに失敗しました。',
          type: 'error',
        })
      } finally {
        setAssigningGuide(false)
      }
    },
    [refetchWeeklyTour, setGroupSelectingResource]
  )

  const handleSelectGroupForResource = React.useCallback(
    async (tourGroupId: number) => {
      if (assigningGuide || !groupSelectingResource) {
        return
      }

      if (!groupSelectingResource.startsWith('tour_guide_')) {
        setGroupSelectedId(tourGroupId)
        return
      }

      const selectedTourGuide = validateAndGetSelectedTourGuide()
      if (!selectedTourGuide) {
        return
      }

      await assignGuideToTourGroup(tourGroupId, selectedTourGuide.guide_id, tour.id)
    },
    [
      tour.id,
      refetchWeeklyTour,
      assigningGuide,
      groupSelectingResource,
      validateAndGetSelectedTourGuide,
      setGroupSelectedId,
      assignGuideToTourGroup,
    ]
  )

  const getMeetupPlaceData = React.useCallback(
    (tourGroup: IBuildedTourGroup) => {
      const hasPrivateOtaReservation =
        tour.post.post_type === 'private' && tourGroup.assignedOtaReservations?.length > 0

      if (!hasPrivateOtaReservation) {
        return null
      }

      const {
        meetup_place,
        custom_meetup_point,
        custom_meetup_point_url,
      } = tourGroup.assignedOtaReservations[0]

      if (custom_meetup_point || custom_meetup_point_url) {
        return {
          meetup_point: custom_meetup_point ?? null,
          meetup_point_url: custom_meetup_point_url ?? null,
        }
      }

      return meetup_place
        ? {
            meetup_point: meetup_place.meetup_point,
            meetup_point_url: meetup_place.meetup_point_url,
          }
        : null
    },
    [tour.post.post_type]
  )

  const handleAddTourGroup = React.useCallback(async () => {
    const newTourGroups = tour.tour_groups
    let tourId = tour.id

    if (!tourId) {
      const { tour: createdTour } = await tourService.createTour({
        tour: { day: tour.day, start_time: tour.start_time, post_id: tour.post_id },
      })
      tourId = createdTour.id
    }
    const { tour_group } = await tourService.createTourGroup({ tour_group: { tour_id: tourId } })
    newTourGroups.push(tour_group)
    onTourUpdate({ ...tour, id: tourId, tour_groups: newTourGroups })
  }, [tour])

  const handleDeleteTourGroup = React.useCallback(
    async id => {
      // 打診・正式依頼でこのグループを指定しているガイドがいる場合は確認する(削除すると「グループ未指定」になる)
      const hasRequestedGuides = tour.tour_guides.some(
        tg =>
          tg.requested_tour_group_id === id &&
          constants.REQUESTING_STATUSES.includes(tg.request_status)
      )
      if (
        hasRequestedGuides &&
        !confirm(
          'このツアーグループを指定して打診・正式依頼中のガイドがいます。削除するとガイドのグループ指定が外れますが、削除しますか？'
        )
      ) {
        return
      }
      try {
        const { flush } = await tourService.deleteTourGroup(id)
        window.flashMessages.addMessage({
          text: flush.message,
          type: flush.type,
        })
        refetchWeeklyTour(tour.id)
      } catch (e) {
        console.error(e)
        window.flashMessages.addMessage({
          text: 'ツアーグループの削除に失敗しました。',
          type: 'error',
        })
      }
    },
    [tour, refetchWeeklyTour]
  )

  const handleRemoveGuideFromTourGroup = React.useCallback(
    async tourGroupId => {
      const otaReservation = tour.ota_reservations.find(
        _otaReservation => _otaReservation.jwt_tour_group_id === tourGroupId
      )
      const isConnected = otaReservation?.connection?.status === 'connected'
      try {
        // Pツアー つなぐ 後の場合は専用メッセージを表示
        const msg = isConnected
          ? '本当に解除しますか？（解除メールが送信されます）'
          : '本当にガイドを解除しますか？'
        if (!confirm(msg)) {
          return
        }

        await tourService.unassignGuide(tourGroupId)
      } catch (e) {
        console.error(e)
        alert('ガイドの解除に失敗しました。')
      }
      refetchWeeklyTour(tour.id)
    },
    [tour.id, tour.ota_reservations, refetchWeeklyTour]
  )

  const handleConnect = React.useCallback(
    async (e, tourGroup: IBuildedTourGroup, otaReservation?: IWeeklyOtaReservation) => {
      const { connectMessage, reConnectMessage } = getConnectionConfirmMessage(tourGroup)
      if (!tourGroup.connected && tourGroup.allTaskDone && !waitConnecting) {
        setWaitConnecting(true)
        try {
          window.globalModal.showModal({
            title: 'つなぐ確認',
            body: (
              <ConnectionMessageModalBody
                title={connectMessage}
                id={otaReservation.id}
                connected={tourGroup.connected}
                connectionMailTemplateId={otaReservation.connection_mail_template_id}
                onSubmit={async ({ subject, body, sendMail }) => {
                  const {
                    tour_group: updatedTourGroup,
                    flush,
                  } = await agentService.connectTourGroup(tourGroup.id, {
                    subject,
                    body,
                    send_mail: sendMail,
                  })
                  let newTour = tour
                  updatedTourGroup.ota_reservations.map(ota_reservation => {
                    newTour = updatedTourByOtaReservation(newTour, ota_reservation.id, {
                      connection: ota_reservation.connection,
                    })
                  })
                  onTourUpdate(newTour)
                  window.flashMessages.addMessage({
                    text: flush.message,
                    type: flush.type,
                  })
                }}
              />
            ),
          })
        } catch (e) {
          console.error(e)
        } finally {
          setWaitConnecting(false)
        }
      } else {
        alert(reConnectMessage)
        e.stopPropagation()
      }
    },
    [tour]
  )

  const cancelFormalRequest = React.useCallback(
    (id, guide, payment_detail) => {
      window.globalModal.showModal({
        title: '本当に正式依頼をキャンセルしますか？',
        small: false,
        body: (
          <CancelFormalRequestsModalBody
            agent={currentUser}
            guide={guide}
            selectedStartTime={tour.start_time}
            selectedTour={{ label: tour.post.name, value: tour.post.id }}
            selectedDate={moment(tour.day)}
            tourGuideId={id}
            tour={tour}
            tourGroup={selectedTourGroup}
            paymentDetail={payment_detail}
            close={() => {
              window.globalModal.closeModal()
              refetchWeeklyTour(tour.id)
            }}
          />
        ),
      })
    },
    [tour]
  )

  const cancelAllSoundOuts = () => {
    window.globalModal.showModal({
      title: '全ての打診中ガイドをキャンセルしますか？',
      body: <CancelSoundoutModalBody tour={tour} />,
    })
  }

  const cancelSoundOut = (tourGuideId: number, guide) => {
    window.globalModal.showModal({
      title: `${guide.fullname || '本名未設定'}（${guide.username}）の打診をキャンセルしますか？`,
      body: <CancelSoundoutModalBody tour={tour} tourGuideId={tourGuideId} />,
    })
  }

  const sendSoundoutOrFormalRequestToCanceledGuide = React.useCallback(
    (id, guide) => {
      const latestConflictingTour = getLatestConflictTour(
        tour.id,
        guide.id,
        guide.conflicting_tour_ids,
        allTours
      )

      window.globalModal.showModal({
        small: true,
        body: (
          <SoundoutOrFormalRequestModalBody
            agent={currentUser}
            guide={guide}
            tour={tour}
            messageType={'formal'}
            onTourUpdate={onTourUpdate}
            resend={true}
            latestConflictingTour={latestConflictingTour}
          />
        ),
      })
    },
    [tour]
  )

  return (
    <LeftPanelContainer
      onClick={e => {
        // Only trigger when clicking on the container itself (white space), not child elements
        if (e.target === e.currentTarget) {
          // Clear all selection state like handleClearSelect does
          setSelectedOtaReservations(sortedOtaReservations(tour.ongoingOtaReservations, orderDesc))
          setSelectedTourGroup(null)
          setSelectedGroupName(null)
        }
      }}
    >
      <div className="title">
        <div className="category">ツアーグループ</div>
        <div className="titleRight">
          <div className="count">{tour.tour_groups.length}</div>
          <div
            className="addTourGroupBtn"
            onClick={handleAddTourGroup}
            role="button"
            aria-label="create tour group"
          >
            <SvgIcon name="add_circle" className="icon" ariaHidden={true} role="presentation" />
          </div>
        </div>
      </div>
      <div className="tgContainer">
        {tour.tour_groups.length === 0 && (
          <>
            <div className="emptyMessage">ツアーグループが存在しません</div>
            <div
              className="addTourGroupLink"
              onClick={handleAddTourGroup}
              role="button"
              aria-label="create tour group"
            >
              <span>ツアーグループを作成する</span>
              <span>
                右上の
                <SvgIcon name="add_circle" className="icon" ariaHidden={true} role="presentation" />
                ボタンからも作成が可能です
              </span>
            </div>
          </>
        )}
        {tour.tour_groups.map(tour_group => {
          const tourGroupClassName = getTourGroupStyleClass(tour_group)
          const meetupPlaceData = getMeetupPlaceData(tour_group)
          // ゲスト選択中などに「ここに追加する」ボタンを表示するか (showingSelectButton)
          const showingSelectButton = !!(
            groupSelectingResource &&
            (groupSelectingResource.startsWith('ota_reservation_') || !tour_group.assignedGuide)
          )
          return (
            <div
              className={`tourGroup ${selectedTourGroup?.id === tour_group.id &&
                'selected'} ${tourGroupClassName}`}
              key={tour_group.id}
              onClick={() => {
                if (selectedTourGroup?.id === tour_group.id) {
                  // Clear all selection state like handleClearSelect does
                  setSelectedOtaReservations(
                    sortedOtaReservations(tour.ongoingOtaReservations, orderDesc)
                  )
                  setSelectedTourGroup(null)
                  setSelectedGroupName(null)
                } else {
                  handleSelectTourGroup(tour_group)
                }
              }}
            >
              {!!tour_group.number && (
                <span
                  className="groupNumberBadge tgNumber"
                  title={`ツアーグループ ${tour_group.number}`}
                  aria-label={`ツアーグループ ${tour_group.number}`}
                >
                  {tour_group.number}
                </span>
              )}
              <div className="tourGroupInner">
                {tour_group.assignedGuide ? (
                  <div className="assignedGuide">
                    <Tooltip content={'ガイドを解除する'}>
                      <div
                        onClick={e => {
                          e.stopPropagation()
                          handleRemoveGuideFromTourGroup(tour_group.id)
                        }}
                        className="tgRemove"
                        role="button"
                        aria-label="Unassign from tour group"
                      >
                        <SvgIcon
                          name="remove"
                          className="icon removeIcon"
                          ariaHidden={true}
                          role="presentation"
                        />
                      </div>
                    </Tooltip>
                    <Guide
                      currentUser={currentUser}
                      guide={tour_group.assignedGuide}
                      showDetail={false}
                      tour={tour}
                      tourGroup={tour_group}
                    />
                  </div>
                ) : tour_group.tourGroupStatus === 'alert' ? (
                  <div className="tgStateSounding" aria-label="Sounding Guide">
                    <SvgIcon
                      name="more_horiz"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    <span>ガイド打診中</span>
                  </div>
                ) : (
                  <div className="tgStateUnassined" aria-label="Guide assign required">
                    <SvgIcon
                      name="assignment_late"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    <span>ガイドをアサインしてください</span>
                  </div>
                )}
                <div className="tgGuestCount" aria-label="Guest Number">
                  {tour.post.post_type === 'private' && tour_group.reservedNumber !== 0 && (
                    <>
                      <ConnectTodoIcons {...tour_group} />
                      {(tour_group.connected || tour_group.allTaskDone) &&
                        (tour_group.assignedGuide ? (
                          <ConnectIconButton
                            tour_group={tour_group}
                            waitConnecting={waitConnecting}
                            handleConnect={e =>
                              handleConnect(e, tour_group, tour_group.assignedOtaReservations[0])
                            }
                          />
                        ) : (
                          <Tooltip content={'ガイド未アサインです'} position="left">
                            <ConnectIconButton
                              tour_group={tour_group}
                              waitConnecting={waitConnecting}
                              handleConnect={e =>
                                handleConnect(e, tour_group, tour_group.assignedOtaReservations[0])
                              }
                            />
                          </Tooltip>
                        ))}
                    </>
                  )}
                  <div>
                    <span>{tour_group.reservedNumber}</span> / {tour_group.maxNumber}
                    {(tour_group.adultsNumber > 0 ||
                      tour_group.childrenNumber > 0 ||
                      tour_group.infantsNumber > 0) && (
                      <p className="breakdownCount">
                        (
                        <span className={`breakdown num${tour_group.adultsNumber}`}>
                          {tour_group.adultsNumber}
                        </span>{' '}
                        /{' '}
                        <span className={`breakdown num${tour_group.childrenNumber}`}>
                          {tour_group.childrenNumber}
                        </span>{' '}
                        /{' '}
                        <span className={`breakdown num${tour_group.infantsNumber}`}>
                          {tour_group.infantsNumber}
                        </span>
                        )
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {meetupPlaceData && (
                <WeeklyInfoCard classNames="meetupPlace">
                  {meetupPlaceData.meetup_point && (
                    <p>
                      <LinkableText autoLinkUrls={true}>
                        {meetupPlaceData.meetup_point}
                      </LinkableText>
                    </p>
                  )}
                  {meetupPlaceData.meetup_point_url && (
                    <p>
                      <a
                        href={meetupPlaceData.meetup_point_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {meetupPlaceData.meetup_point_url}
                      </a>
                    </p>
                  )}
                </WeeklyInfoCard>
              )}
              {!tour.excluded_from_payment && tour_group.assignedGuide && tour_group.paymentDetail && (
                <div className="paymentDetailContainer">
                  <div className="paymentDetailLabel">
                    その他手当 ¥<b>{(tour_group.paymentDetail?.other_fee || 0).toLocaleString()}</b>
                  </div>
                  <div
                    className="editPaymentDetailButton"
                    onClick={e => {
                      e.stopPropagation()
                      window.globalModal.showModal({
                        title: 'その他手当',
                        body: (
                          <PaymentDetailModal
                            paymentDetail={tour_group.paymentDetail}
                            refetchWeeklyTour={() => refetchWeeklyTour(tour.id)}
                          />
                        ),
                        handleSubmit: undefined,
                        zIndex: 6001,
                      })
                    }}
                    role="button"
                    aria-label="Open payment detail window"
                  >
                    <SvgIcon name="edit" className="icon" ariaHidden={true} role="presentation" />
                    編集
                  </div>
                </div>
              )}
              <div className="memoRow">
                <Memo
                  target={tour_group}
                  targetClass="tourGroup"
                  internal={true}
                  external={true}
                  handleAfterUpdate={handleAfterUpdateTourGroup}
                />
                {tour_group.assignedGuide && (
                  <div className="guideArrivalRow">
                    <GuideArrivalBadge arrivedAt={tour_group.guideArrivedAt} />
                  </div>
                )}
              </div>
              {tour_group.reservedNumber === 0 && !tour_group.assignedGuide && (
                <div
                  className="removeTourGroupBtn"
                  onClick={() => handleDeleteTourGroup(tour_group.id)}
                  role="button"
                  aria-label="delete tour group"
                >
                  <SvgIcon
                    name="delete_forever"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  <span>削除</span>
                </div>
              )}

              {showingSelectButton && (
                <div
                  className="tgSelectButton"
                  onClick={e => {
                    e.stopPropagation()
                    handleSelectGroupForResource(tour_group.id)
                  }}
                  role="button"
                  aria-label="Assign button"
                >
                  <SvgIcon
                    name="check_circle"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  ここに追加する
                </div>
              )}
              {!groupSelectingResource && (
                <span className="hoverSign" aria-label="Select sign">
                  <SvgIcon
                    name="chevron_right"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                </span>
              )}
            </div>
          )
        })}
      </div>

      {staffGuides.length > 0 && (
        <>
          <div className="title">
            <div className="category">スタッフ（確定済み）</div>
          </div>
          <div className="unAssignedTourGuides">
            {staffGuides.map(({ id, guide, agent_rank, payment_detail }) => (
              <div className="unassignedGuide staffGuide" key={guide.id}>
                <div>
                  <a
                    href={`/agent/guides/${guide.id}/edit`}
                    target="_blank"
                    rel="noopener"
                    aria-label="Guide"
                  >
                    <AvatarImage size={24} avatarURL={guide.avatar_url} />
                    {`${guide.fullname || '本名未設定'}（${guide.username}）`}
                    <SvgIcon
                      name="chevron_right"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                  </a>
                  <span className="rankTag">{agent_rank?.name}</span>
                </div>
                <div className="approvedButtons">
                  <OperationItem
                    className="cancelItem"
                    onClick={() => cancelFormalRequest(id, guide, payment_detail)}
                    role="button"
                  >
                    <SvgIcon
                      name="delete_forever"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    <span>依頼キャンセル</span>
                  </OperationItem>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {[
        { title: '打診中ガイド', guides: allUnassignedGuides, applied: false },
        { title: '応募ガイド', guides: appliedGuides, applied: true },
      ].map(
        ({ title, guides, applied }) =>
          guides.length > 0 && (
            <React.Fragment key={title}>
              <div className="title">
                <div className="category">{title}</div>
                <div className="subHeader">
                  <div className="count">{guides.length}</div>
                </div>
              </div>
              <div className="unAssignedTourGuides">
                {guides.map(
                  ({
                    id,
                    guide,
                    request_status,
                    agent_rank,
                    payment_detail,
                    requested_tour_group_id,
                    created_at,
                  }) => (
                    <div className="unassignedGuide" key={guide.id}>
                      <div>
                        <span className={`status ${request_status}`}>
                          {request_status === 'guide_applied' ? (
                            <span aria-label="Guide Applied">
                              <SvgIcon
                                name="front_hand"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              応募
                            </span>
                          ) : request_status === 'agent_pre_requested' ? (
                            <span aria-label="Sounding Guide">
                              <SvgIcon
                                name="more_horiz"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              打診中
                            </span>
                          ) : request_status === 'guide_approved' ? (
                            <span aria-label="Guide Accepted">
                              <SvgIcon
                                name="person_filled"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              承認済
                            </span>
                          ) : (
                            <span aria-label="guide ready to assign">
                              <SvgIcon
                                name="check"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              依頼済
                            </span>
                          )}
                          {isNumberedTour && requestedTourGroupBadge(requested_tour_group_id)}
                        </span>
                        {agent_rank && <span className="rankTag">{agent_rank.name}</span>}
                        <a
                          href={`/agent/guides/${guide.id}/edit`}
                          target="_blank"
                          rel="noopener"
                          aria-label="Guide"
                        >
                          <AvatarImage size={24} avatarURL={guide.avatar_url} />
                          {`${guide.fullname || '本名未設定'}（${guide.username}）`}
                          <SvgIcon
                            name="chevron_right"
                            className="icon"
                            ariaHidden={true}
                            role="presentation"
                          />
                        </a>
                        <span className="tourInfo">催行ツアー数 {guide.tour_guide_count}</span>
                      </div>
                      {request_status !== 'agent_official_requested' && created_at && (
                        <div className="requestDate">
                          {applied ? '応募日時' : '打診日時（登録日時）'}：
                          {moment(created_at)
                            .utcOffset(9)
                            .format('YYYY/MM/DD HH:mm')}
                        </div>
                      )}
                      {['agent_pre_requested', 'guide_approved', 'guide_applied'].includes(
                        request_status
                      ) ? (
                        <div className="approvedButtons">
                          <a
                            className="formalRequestButton"
                            role="button"
                            onClick={() => showMessageModal(guide)}
                          >
                            <SvgIcon
                              name="chat"
                              className="icon"
                              ariaHidden={true}
                              role="presentation"
                            />
                            正式依頼を送る
                          </a>
                          {applied ? (
                            <OperationItem
                              className={`cancelItem ${rejectingGuide ? 'disabled' : ''}`}
                              onClick={() =>
                                rejectGuide(
                                  id,
                                  `${guide.fullname || '本名未設定'}（${guide.username}）`
                                )
                              }
                              role="button"
                              aria-disabled={rejectingGuide}
                            >
                              <SvgIcon
                                name="block"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              見送る
                            </OperationItem>
                          ) : (
                            <OperationItem
                              className="cancelItem"
                              onClick={() => cancelSoundOut(id, guide)}
                              role="button"
                            >
                              <SvgIcon
                                name="delete_forever"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              打診キャンセル
                            </OperationItem>
                          )}
                        </div>
                      ) : (
                        <div className="approvedButtons">
                          {/* アサイン先のグループ選択中は、同じボタンで選択状態を解除できるようにする */}
                          {groupSelectingResource === `tour_guide_${id}` ? (
                            <OperationItem
                              className="cancelItem"
                              onClick={() => setGroupSelectingResource(null)}
                              role="button"
                            >
                              <SvgIcon
                                name="close_circle"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              アサイン中止
                            </OperationItem>
                          ) : (
                            <OperationItem
                              onClick={() => {
                                handleAssignGuide(id)
                                setShowGuides(true)
                              }}
                              role="button"
                            >
                              <SvgIcon
                                name="add_circle"
                                className="icon"
                                ariaHidden={true}
                                role="presentation"
                              />
                              ツアーグループにアサイン
                            </OperationItem>
                          )}

                          <OperationItem
                            className="cancelItem"
                            onClick={() => cancelFormalRequest(id, guide, payment_detail)}
                            role="button"
                          >
                            <SvgIcon
                              name="delete_forever"
                              className="icon"
                              ariaHidden={true}
                              role="presentation"
                            />
                            <span>依頼キャンセル</span>
                          </OperationItem>
                        </div>
                      )}
                    </div>
                  )
                )}
                {!applied && isPrivate && hasSoundOutedGuides && (
                  <OperationItem
                    className="cancelItem single"
                    onClick={cancelAllSoundOuts}
                    role="button"
                  >
                    <SvgIcon name="delete_forever" ariaHidden={true} role="presentation" />
                    <span>全ての打診中ガイドをキャンセルする</span>
                  </OperationItem>
                )}
              </div>
            </React.Fragment>
          )
      )}
      {tour.canceledTourGuides.length > 0 && (
        <>
          <div className="title">
            <div className="category">キャンセル・見送り済ガイド</div>
            <div className="count">{tour.canceledTourGuides.length}</div>
          </div>
          <div className="canceledTourGuides">
            {tour.canceledTourGuides.map(({ id, guide, request_status }) => (
              <div className="canceledGuide" key={guide.id}>
                <div className="guide_item">
                  <span className={`status ${request_status}`}>
                    {request_status === 'agent_rejected' ? (
                      <span aria-label="Guide Not Selected">
                        <SvgIcon
                          name="block"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        見送り
                      </span>
                    ) : request_status === 'guide_declined' ? (
                      <span aria-label="Guide Declined">
                        <SvgIcon
                          name="close"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        打診拒否
                      </span>
                    ) : request_status === 'agent_pre_canceled' ? (
                      <span aria-label="Sound Out Canceled">
                        <SvgIcon
                          name="delete_forever"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        打診取消
                      </span>
                    ) : (
                      <span aria-label="Request Canceled">
                        <SvgIcon
                          name="delete_forever"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        依頼キャンセル
                      </span>
                    )}
                  </span>
                  <a
                    href={`/agent/guides/${guide.id}/edit`}
                    target="_blank"
                    rel="noopener"
                    aria-label="Guide"
                  >
                    <AvatarImage size={24} avatarURL={guide.avatar_url} />
                    {`${guide.fullname || '本名未設定'}（${guide.username}）`}
                    <SvgIcon
                      name="chevron_right"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                  </a>
                  <span className="tourInfo">催行ツアー数 {guide.tour_guide_count}</span>
                </div>
                <OperationItem
                  onClick={() => sendSoundoutOrFormalRequestToCanceledGuide(id, guide)}
                  role="button"
                >
                  <SvgIcon name="redo" className="icon" ariaHidden={true} role="presentation" />
                  再依頼する
                </OperationItem>
              </div>
            ))}
          </div>
        </>
      )}
      {tour.unassignedOtaReservations.length > 0 && (
        <>
          <div className="title">
            <div className="category">グループ未アサインのゲスト</div>
            <div className="count">{tour.unassignedOtaReservations.length}</div>
          </div>
          <div
            className={`unassignedReservations ${selectedGroupName ===
              'グループ未アサインのゲスト' && 'selected'}`}
            onClick={handleSelectUnassignedOtaReservations}
          >
            {tour.unassignedOtaReservations.map(otaReservation => (
              <a
                href={`/agent/ota_reservations/${otaReservation.id}`}
                target="_blank"
                key={otaReservation.id}
                aria-label="to Guest info"
              >
                {otaReservation.custom_guest_name || otaReservation.customer_full_name}
                <SvgIcon
                  name="chevron_right"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              </a>
            ))}
            <span className="hoverSign" aria-label="Select sign">
              <SvgIcon
                name="chevron_right"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            </span>
          </div>
        </>
      )}
      {tour.canceledOtaReservations.length > 0 && (
        <>
          <div className="title">
            <div className="category">キャンセル済のゲスト</div>
            <div className="count">{tour.canceledOtaReservations.length}</div>
          </div>
          <div
            className={`canceledReservations ${selectedGroupName === 'キャンセル済のゲスト' &&
              'selected'}`}
            onClick={handleSelectCanceledOtaReservations}
            role="button"
          >
            {tour.canceledOtaReservations.map(otaReservation => (
              <a
                href={`/agent/ota_reservations/${otaReservation.id}`}
                target="_blank"
                key={otaReservation.id}
                aria-label="to Guest info"
              >
                {otaReservation.custom_guest_name || otaReservation.customer_full_name}
                <SvgIcon
                  name="chevron_right"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              </a>
            ))}
            <span className="hoverSign" aria-label="Select sign">
              <SvgIcon
                name="chevron_right"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            </span>
          </div>
        </>
      )}
    </LeftPanelContainer>
  )
}

const LeftPanelContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 40px;
  margin-right: -1px;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 1;
  overscroll-behavior: contain;
  background-color: ${constants.COLORS.background2};
  border-right: 1px solid ${constants.COLORS.divider};
  .subHeader {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  .emptyMessage {
    padding: 24px 16px 16px;
    color: ${constants.COLORS.subText};
    font-size: 14px;
    text-align: center;
  }

  .icon {
    flex-shrink: 0;
  }

  .tgContainer {
    // 1枚目のカード上端に重ねた番号バッジが sticky タイトルに隠れないよう、上に余白を取る
    padding: 0 16px 4px;
    .addTourGroupLink {
      cursor: pointer;
      margin-bottom: 32px;
      text-align: center;
      span {
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${constants.COLORS.subText};
        font-size: 12px;
        &:first-child {
          display: block;
          text-decoration: underline;
          font-size: 13px;
          color: ${constants.COLORS.primary};
        }
        .icon {
          width: 16px;
          height: 16px;
          color: ${constants.COLORS.divider};
        }
      }
      &:hover {
        span:first-child {
          text-decoration: none;
        }
      }
    }
    .assignedGuide {
      position: relative;
      &:hover .tgRemove {
        opacity: 1;
      }
      .tooltipContent {
        transform: none;
        bottom: auto;
        // stickyヘッダーに隠れないよう、上ではなく解除アイコンの右側に表示する
        left: 48px;
        top: -22px;
      }
    }
    .tgRemove {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      // 左上はツアーグループ番号のバッジがあるため、アバターの右上に置く
      top: -20px;
      left: 22px;
      width: 20px;
      height: 20px;
      color: ${constants.COLORS.danger};
      background-color: ${constants.COLORS.white};
      border: 2px solid ${constants.COLORS.danger};
      cursor: pointer;
      transition: opacity 0.2s;
      border-radius: 50%;
      opacity: 0;
      .icon {
        width: 16px;
        height: 16px;
        margin-right: 0;
      }
    }
  }

  .hoverSign {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    left: 0;
    width: 20px;
    height: 24px;
    background-color: ${constants.COLORS.primary};
    border-radius: 0 12px 12px 0;
    transform: translateY(-50%);
    transition: opacity 0.25s;
    opacity: 0;
    z-index: 2;
    .icon {
      color: ${constants.COLORS.white};
      width: 20px;
      height: 20px;
    }
  }

  @keyframes slidein {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .tourGroup {
    position: relative;
    line-height: 24px;
    padding: 12px 16px;
    margin-bottom: 12px;
    background-color: ${constants.COLORS.white};
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 12px;
    transition: background-color 0.2s, border-color 0.2s;
    cursor: pointer;

    &.ok {
      background-color: ${constants.COLORS.white};
    }
    &.caution {
      background-color: ${constants.COLORS.successBG2};
      .tourGroupInner {
        .tgGuestCount {
          span {
            color: ${constants.COLORS.primary};
          }
        }
      }
    }
    &.ng {
      color: ${constants.COLORS.danger};
      background-color: ${constants.COLORS.tourGuideMissingBG};
      border-color: ${constants.COLORS.dangerBG};
    }
    &.empty {
      background-color: ${constants.COLORS.lightGray};
    }
    &:hover {
      .hoverSign {
        opacity: 1;
      }
    }

    // カードに色が付いている状態では、Meetup Place の緑背景が馴染まないため白背景にする
    &.caution,
    &.ng,
    &.empty,
    &.selected {
      .meetupPlace {
        background: ${constants.COLORS.white};
        border-color: ${constants.COLORS.divider2};
      }
    }

    &.selected {
      border-color: ${constants.COLORS.primary};
      background-color: ${constants.COLORS.successBG2};
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 8px;
        height: 100%;
        border-radius: 11px 0 0 11px;
        background-color: ${constants.COLORS.primary};
        z-index: 1;
        animation: slidein 0.25s ease-out;
      }
      .hoverSign {
        opacity: 1;
      }
    }

    // ツアーグループカード左上(アバター左上)の番号バッジ位置
    // カードの左上の角(枠線の上)に付箋のように重ねる
    .tgNumber {
      position: absolute;
      top: -1px;
      left: -4px;
    }

    // ゲスト選択中のみ表示。カードの高さが変わらないよう余白は取らず、最下行(削除ボタン・ガイド到着)の上にかぶせる
    .tgSelectButton {
      position: absolute;
      right: 0;
      bottom: 12px;
      z-index: 3;
      line-height: 20px;
      padding: 4px 12px 4px 4px;
      color: ${constants.COLORS.white};
      font-size: 13px;
      background-color: ${constants.COLORS.primary};
      animation: slideinTGButton 0.25s ease-in-out;
      border-radius: 14px 0 0 14px;
      cursor: pointer;
      .icon {
        width: 20px;
        height: 20px;
        margin-right: 4px;
        opacity: 0.25;
        transition: opacity 0.2s;
      }
      &:hover {
        .icon {
          opacity: 1;
        }
      }
    }
    .memoRow {
      position: relative;
      display: flex;
      margin: 8px -16px 0;
      padding: 12px 16px 0;
      border-top: 1px solid ${constants.COLORS.divider2};
      // メモ本文の省略表示(ellipsis)を効かせるため、flex子要素の最小幅を0にする
      > div:first-child {
        flex: 1 1 auto;
        min-width: 0;
      }
      // ガイド到着インジケータはメモの幅を奪わないよう、右下(メモボタンの行)に絶対配置する
      .guideArrivalRow {
        position: absolute;
        right: 16px;
        bottom: -4px;
      }
    }
    .removeTourGroupBtn {
      position: relative;
      display: flex;
      align-items: center;
      float: right;
      margin-top: -20px;
      color: ${constants.COLORS.subText};
      transition: color 0.2s;
      cursor: pointer;
      z-index: 1;
      svg {
        width: 20px;
        height: 20px;
      }
      span {
        margin-left: 4px;
        font-size: 12px;
      }
      &:hover {
        color: ${constants.COLORS.primary};
      }
    }
    @keyframes slideinTGButton {
      0% {
        transform: translateX(100%);
        opacity: 0;
      }
      100% {
        transform: translateX(0);
        opacity: 1;
      }
    }
  }

  .tourGroupInner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    > div {
      display: flex;
      align-items: center;
      .icon {
        flex: 0 0 auto;
        margin-right: 8px;
      }
    }
    .tgStateSounding,
    .tgStateSounding span {
      color: ${constants.COLORS.tourGuideSoundoutText};
    }
    .tgStateUnassined {
      .icon {
        color: ${constants.COLORS.danger};
      }
      .toggleGuideSearch {
        line-height: 16px;
        color: ${constants.COLORS.primary};
        font-size: 12px;
        text-decoration: underline;
        cursor: pointer;
        &:hover {
          text-decoration: none;
        }
      }
    }
    .tgGuestCount {
      flex: none;
      line-height: 1.6rem;
      color: ${constants.COLORS.subText};
      font-size: 12px;
      > div {
        text-align: right;
      }
    }
    span {
      font-size: 14px;
      margin-right: 2px;
      &.empty,
      &.alert {
        color: ${constants.COLORS.subText};
      }
    }
    .breakdownCount {
      display: flex;
      align-items: center;
      font-size: 11px;
    }
    span.breakdown {
      font-size: 11px;
      font-weight: bold;
      color: ${constants.COLORS.primary};
      margin: 0 2px;
      &.num0 {
        font-weight: normal;
        color: ${constants.COLORS.subText};
      }
    }
  }
  .paymentDetailContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 8px -16px 0;
    padding: 8px 16px;
    border-top: 1px solid ${constants.COLORS.divider2};
    & + .memoRow {
      margin-top: 0;
    }
    .paymentDetailLabel {
      font-size: 12px;
      color: ${constants.COLORS.subText};
      b {
        margin-left: 4px;
        color: ${constants.COLORS.black};
        font-weight: bold;
      }
    }
    .editPaymentDetailButton {
      display: flex;
      align-items: center;
      line-height: 16px;
      padding: 4px;
      color: ${constants.COLORS.primary};
      font-size: 12px;
      cursor: pointer;
      transition: color 0.2s;
      &:hover {
        color: ${constants.COLORS.secondary};
      }
      .icon {
        width: 16px;
        height: 16px;
        margin-right: 4px;
      }
    }
  }
  // ツアーグループ番号のバッジ(グループカード・打診中ガイドで共通の見た目)
  .groupNumberBadge {
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    line-height: 18px;
    color: ${constants.COLORS.black};
    font-size: 10px;
    font-weight: bold;
    text-align: center;
    background-color: ${constants.COLORS.divider2};
    border: 1px solid ${constants.COLORS.white};
    border-radius: 2px;
    box-sizing: border-box;
    z-index: 2;
    &.unspecified {
      background-color: ${constants.COLORS.white};
      border: 1px dashed ${constants.COLORS.divider};
    }
  }
  .unassignedGuide .groupNumberBadge {
    flex: 0 0 auto;
    justify-content: center;
  }

  .unAssignedTourGuides,
  .canceledTourGuides,
  .unassignedReservations,
  .canceledReservations {
    margin: 0 16px 12px;
    background-color: ${constants.COLORS.white};
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 12px;
    transition: background-color 0.2s, border-color 0.2s;
    &.selected {
      border-color: ${constants.COLORS.primary};
    }
  }
  .unAssignedTourGuides {
    .single {
      padding: 8px 16px;
      &.cancelItem {
        padding: 12px 16px;
      }
    }
    .unassignedGuide {
      font-size: 14px;
      padding: 12px 16px;
      border-bottom: 1px solid ${constants.COLORS.divider2};
      text-align: right;
      &:last-child {
        border-bottom: 0;
      }
      .status {
        display: flex;
        align-items: center;
        gap: 4px;
        line-height: 16px;
        padding: 2px 4px;
        // 番号バッジ(高さ20px)の有無でタグの高さが変わらないよう、バッジ込みの高さを最小値にする
        min-height: 24px;
        box-sizing: border-box;
        margin-right: 4px;
        font-size: 11px;
        border-radius: 2px;
        span {
          display: flex;
          align-items: center;
          white-space: nowrap;
        }
        .icon {
          width: 16px;
          height: 16px;
          margin-right: 4px;
        }
        // 打診中
        &.agent_pre_requested {
          color: ${constants.COLORS.tourGuideSoundoutText};
          background-color: ${constants.COLORS.tourGuideSoundoutBG};
        }
        &.guide_applied {
          color: ${constants.COLORS.info2};
          background-color: ${constants.COLORS.infoBG};
        }
        // 承認
        &.guide_approved {
          color: ${constants.COLORS.info2};
          background-color: ${constants.COLORS.infoBG};
        }
        // 正式依頼
        &.agent_official_requested {
          color: ${constants.COLORS.primary};
          background-color: ${constants.COLORS.successBG2};
        }
      }
      > div {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        text-align: left;
        gap: 4px;
        a {
          display: flex;
          align-items: center;
          color: ${constants.COLORS.black};
          transition: color 0.2s;
          &:hover {
            color: ${constants.COLORS.primary};
          }
          > div {
            margin-right: 8px;
          }
        }
        .tourInfo {
          color: ${constants.COLORS.subText};
          font-size: 12px;
        }
        // 特殊ランク名
        .rankTag {
          line-height: 16px;
          padding: 4px 8px;
          margin-right: 8px;
          font-size: 11px;
          border-radius: 2px;
          background-color: ${constants.COLORS.infoBG2};
        }
      }
      > a,
      .redoButton {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-right: 0;
        color: ${constants.COLORS.primary};
        font-size: 12px;
        text-decoration: none;
        cursor: pointer;
        vertical-align: middle;
        &:hover {
          text-decoration: none;
        }
        > .icon {
          width: 20px;
          height: 20px;
        }
      }
      .requestDate {
        margin-top: 4px;
        color: ${constants.COLORS.subText};
        font-size: 12px;
      }
      .approvedButtons .formalRequestButton {
        display: inline-flex;
        align-items: center;
        color: ${constants.COLORS.primary};
        font-size: 12px;
        cursor: pointer;
        .icon {
          width: 20px;
          height: 20px;
          margin-right: 4px;
        }
      }
      .approvedButtons {
        display: inline-flex;
        gap: 8px;
        margin-top: 4px;
        margin-right: 0;
      }
      // スタッフ(確定済み): 名前とランク名を1行(ランク名は右寄せ)
      &.staffGuide > div {
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 4px 8px;
        .rankTag {
          margin-right: 0;
          white-space: nowrap;
        }
      }
      .removeButton {
        display: inline-flex;
        gap: 4px;
        align-items: center;
        margin-right: 0;
        color: ${constants.COLORS.subText};
        font-size: 12px;
        text-decoration: none;
        cursor: pointer;
        &:hover {
          text-decoration: none;
        }
        > .icon {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
  .canceledTourGuides {
    position: relative;
    cursor: pointer;
    &.selected {
      background-color: ${constants.COLORS.successBG2};
      .hoverSign {
        opacity: 1;
      }
    }
    &:hover {
      .hoverSign {
        opacity: 1;
      }
    }
    a {
      display: inline-flex;
      align-items: center;
      color: ${constants.COLORS.black};
      font-size: 14px;
      transition: color 0.2s;
      &:last-child {
        margin-right: 0;
      }
      &:hover {
        color: ${constants.COLORS.primary};
      }
      .icon {
        width: 20px;
        height: 20px;
      }
    }
    .canceledGuide {
      font-size: 14px;
      padding: 12px 16px;
      border-bottom: 1px solid ${constants.COLORS.divider2};
      text-align: right;
      &:last-child {
        border-bottom: 0;
      }

      .guide_item {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        text-align: left;
        gap: 4px 8px;
        .status {
          line-height: 16px;
          padding: 4px;
          font-size: 11px;
          border-radius: 2px;
          color: ${constants.COLORS.cautionText};
          background-color: ${constants.COLORS.cautionBG};
          span {
            display: flex;
            align-items: center;
            white-space: nowrap;
          }
          .icon {
            width: 16px;
            height: 16px;
            margin-right: 4px;
          }
        }
        a {
          display: flex;
          align-items: center;
          color: ${constants.COLORS.subText};
          transition: color 0.2s;
          &:hover {
            color: ${constants.COLORS.primary};
          }
          > div {
            margin-right: 8px;
          }
        }
        .tourInfo {
          color: ${constants.COLORS.subText};
          font-size: 12px;
        }
      }
      > a {
        display: inline-block;
        line-height: 16px;
        margin-right: 0;
        color: ${constants.COLORS.primary};
        font-size: 12px;
        text-decoration: underline;
        cursor: pointer;
        &:hover {
          text-decoration: none;
        }
      }
    }
  }
  .unassignedReservations,
  .canceledReservations {
    position: relative;
    padding: 12px 16px;
    cursor: pointer;
    &.selected {
      background-color: ${constants.COLORS.successBG2};
      .hoverSign {
        opacity: 1;
      }
    }
    &:hover {
      .hoverSign {
        opacity: 1;
      }
    }
    a {
      display: inline-flex;
      align-items: center;
      margin-right: 8px;
      color: ${constants.COLORS.black};
      font-size: 14px;
      transition: color 0.2s;
      &:last-child {
        margin-right: 0;
      }
      &:hover {
        color: ${constants.COLORS.primary};
      }
      .icon {
        width: 20px;
        height: 20px;
      }
    }
  }
`

const OperationItem = styled.div`
  font-size: 12px;
  cursor: pointer;
  color: ${constants.COLORS.primary};
  &:hover {
    opacity: 0.75;
  }
  .icon {
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
  &.single {
    text-align: center;
    line-height: 2;
  }
  &.cancelItem {
    color: ${constants.COLORS.subText};

    &:hover {
      color: ${constants.COLORS.primary};
      opacity: 1;
    }
  }
  &.disabled {
    color: ${constants.COLORS.subText};
    cursor: auto;
    &:hover {
      color: ${constants.COLORS.subText};
      opacity: 1;
    }
  }
`
